import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/UnifiedAuthContext";
import { Mail, Key, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";

const LoginForm = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [selectedMethod, setSelectedMethod] = useState<"password" | "magic_link" | null>(null);
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const { login, signInWithMagicLink, loading, error: authError } = useAuth();
  const navigate = useNavigate();

  const handlePasswordChoice = () => {
    if (!email) {
      setLocalError(t('auth.enter_email_error'));
      return;
    }
    setLocalError(null);
    setSelectedMethod("password");
  };

  const handleMagicLinkChoice = async () => {
    if (!email) {
      setLocalError(t('auth.enter_email_error'));
      return;
    }
    setLocalError(null);

    // Сразу отправляем, так как кнопка выбора метода и есть действие
    try {
      const { error } = await signInWithMagicLink(email);
      if (error) throw error;
      setMagicLinkSent(true);
      setSelectedMethod("magic_link");
    } catch (err) {
      console.error("Magic link error:", err);
      // Ошибка уже должна быть обработана в сторе и попасть в authError
    }
  };

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    if (!email || !password) {
      setLocalError(t('auth.fill_all_fields_error'));
      return;
    }

    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      console.error("Login failed:", err);
    }
  };

  const resetState = () => {
    setSelectedMethod(null);
    setMagicLinkSent(false);
    setLocalError(null);
    setPassword("");
  };

  // Рендер: Magic Link отправлен
  if (magicLinkSent) {
    return (
      <div className="space-y-6 w-full max-w-md text-center animate-in fade-in slide-in-from-bottom-4">
        <div className="w-16 h-16 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto">
          <CheckCircle2 className="w-8 h-8" />
        </div>

        <div className="space-y-2">
          <h2 className="text-2xl font-bold">{t('auth.check_email_title')}</h2>
          <p className="text-muted-foreground">
            <Trans
              i18nKey="auth.sent_magic_link_to"
              values={{ email }}
              components={{ 1: <span className="font-medium text-foreground" /> }}
            />
          </p>
          <p className="text-sm text-muted-foreground">
            {t('auth.click_link_instruction')}
          </p>
        </div>

        <Button
          variant="outline"
          onClick={resetState}
          className="w-full"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('auth.back_button')}
        </Button>
      </div>
    );
  }

  // Рендер: Вход по паролю
  if (selectedMethod === "password") {
    return (
      <div className="space-y-6 w-full max-w-md animate-in fade-in slide-in-from-right-4">
        <Button
          variant="ghost"
          onClick={() => setSelectedMethod(null)}
          className="p-0 h-auto hover:bg-transparent text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('auth.back_to_method_selection')}
        </Button>

        <div className="space-y-1">
          <h2 className="text-xl font-semibold">{t('auth.password_login_title')}</h2>
          <p className="text-sm text-muted-foreground">{t('auth.for_account', { email })}</p>
        </div>

        <form onSubmit={handlePasswordLogin} className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Link to="/forgot-password" className="text-xs text-brand-600 hover:underline">
                {t('auth.forgot_password')}
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
            />
          </div>

          {(localError || authError) && (
            <div className="text-sm text-red-500 font-medium bg-red-50 p-3 rounded-md">
              {localError || authError}
            </div>
          )}

          <Button
            type="submit"
            className="w-full"
            disabled={loading}
          >
            {loading ? t('auth.logging_in') : t('auth.login.label')}
          </Button>

          <div className="text-center text-sm pt-2">
            <span className="text-muted-foreground">{t('auth.prefer_no_password')} </span>
            <button
              type="button"
              onClick={handleMagicLinkChoice}
              className="text-brand-600 font-medium hover:underline bg-transparent border-none p-0 cursor-pointer"
              disabled={loading}
            >
              {t('auth.send_magic_link')}
            </button>
          </div>
        </form>
      </div>
    );
  }

  // Рендер: Начальный экран (Ввод email + Выбор метода)
  return (
    <div className="space-y-6 w-full max-w-md">
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t('auth.email')}</Label>
          <Input
            id="email"
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setLocalError(null);
            }}
            required
          />
        </div>

        {(localError || authError) && (
          <div className="text-sm text-red-500 font-medium bg-red-50 p-3 rounded-md">
            {localError || authError}
          </div>
        )}

        <div className="space-y-3 pt-2">
          <button
            type="button"
            onClick={handleMagicLinkChoice}
            disabled={!email || loading}
            className="w-full flex items-center p-4 border rounded-lg hover:bg-accent hover:border-accent-foreground/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed group text-left"
          >
            <div className="bg-primary/10 p-3 rounded-full mr-4 group-hover:bg-primary/20 transition-colors">
              <Mail className="w-6 h-6 text-primary" />
            </div>
            <div>
              <div className="font-semibold">{t('auth.magic_link_login_title')}</div>
              <div className="text-sm text-muted-foreground">{t('auth.magic_link_login_desc')}</div>
            </div>
          </button>

          <button
            type="button"
            onClick={handlePasswordChoice}
            disabled={!email || loading}
            className="w-full flex items-center p-4 border rounded-lg hover:bg-accent hover:border-accent-foreground/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed group text-left"
          >
            <div className="bg-primary/10 p-3 rounded-full mr-4 group-hover:bg-primary/20 transition-colors">
              <Key className="w-6 h-6 text-primary" />
            </div>
            <div>
              <div className="font-semibold">{t('auth.password_login_title')}</div>
              <div className="text-sm text-muted-foreground">{t('auth.password_login_desc')}</div>
            </div>
          </button>
        </div>
      </div>

      <div className="text-center text-sm">
        <span className="text-muted-foreground">{t('auth.no_account')}</span>{" "}
        <Link to="/register" className="text-brand-600 font-medium hover:underline">
          {t('auth.register_link')}
        </Link>
      </div>
    </div>
  );
};

export default LoginForm;